import { Alert as MuiAlert, type SxProps, type Theme } from "@mui/material";
import { styled } from "@mui/material/styles";

import { type AlertSeverity } from "ui/alert/alert.types";

import { alertTone } from "theme";

/** Figma's `alert/progress bar/h` and `/opacity`. */
const PROGRESS_HEIGHT = 6;
const PROGRESS_OPACITY = 0.5;

/** Figma's `alert/container/icon-text/py`: the collapsible variant's title row is 2px taller. */
const COLLAPSIBLE_ROW_PADDING = 0.25;

type AlertRootProps = {
  isCollapsible: boolean;
  isCollapsed: boolean;
  hasProgress: boolean;
};

const ROOT_PROPS: ReadonlyArray<PropertyKey> = ["isCollapsible", "isCollapsed", "hasProgress"];

/**
 * The surface every alert shares is `MuiAlert` in the theme; these are only the variants `ui/alert`
 * adds. `styled()` gets no `sx` shorthands, so every value is a `theme.*` lookup.
 */
export const AlertRoot = styled(MuiAlert, {
  shouldForwardProp: (prop) => !ROOT_PROPS.includes(prop),
})<AlertRootProps>(({ theme }) => ({
  variants: [
    {
      props: { isCollapsible: true },
      style: {
        "& .MuiAlert-icon": {
          paddingTop: theme.spacing(COLLAPSIBLE_ROW_PADDING),
          paddingBottom: theme.spacing(COLLAPSIBLE_ROW_PADDING),
        },
        "& .MuiAlert-message": {
          paddingTop: theme.spacing(COLLAPSIBLE_ROW_PADDING),
          paddingBottom: theme.spacing(COLLAPSIBLE_ROW_PADDING),
        },
        // The 4px under the title moves inside the collapsing body, so a folded alert has no tail.
        "& .MuiAlertTitle-root": { marginBottom: 0 },
      },
    },
    {
      props: { isCollapsed: true },
      style: { alignItems: "center" },
    },
    {
      props: { hasProgress: true },
      style: {
        position: "relative",
        // The bar runs into the rounded bottom-right corner; at 100% it would square it off.
        overflow: "hidden",
        paddingBottom: `calc(${theme.spacing(2)} + ${PROGRESS_HEIGHT}px)`,
      },
    },
  ],
}));

export const AlertBody = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(0.5),
}));

export const AlertActions = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(1.5),
}));

type AlertProgressProps = { severity: AlertSeverity; value: number };

/**
 * Absolute against the root's padding box, so it starts after the stripe and runs to the right edge
 * whatever the message slot's width. The message slot's `overflow: auto` does not clip it: the root,
 * not the slot, is its containing block.
 */
export const AlertProgress = styled("div", {
  shouldForwardProp: (prop) => prop !== "severity" && prop !== "value",
})<AlertProgressProps>(({ theme, severity, value }) => ({
  position: "absolute",
  left: 0,
  bottom: 0,
  height: PROGRESS_HEIGHT,
  width: `${value}%`,
  backgroundColor: alertTone(theme, severity).light,
  opacity: PROGRESS_OPACITY,
}));

/** A 24px hit target over Figma's 16px box; the theme's action-slot margins centre it there. */
export const ACTION_BUTTON_SX: SxProps<Theme> = {
  width: 24,
  height: 24,
  p: 0,
};
